import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { toast } from "react-toastify";
import AppModal from "../../../../Components/Common/AppModal";
import {
  getMasterEnvVarList,
  createMasterEnvVar,
  updateMasterEnvVar,
  deleteMasterEnvVar,
  createMasterEnvVarValue,
  updateMasterEnvVarValue,
  deleteMasterEnvVarValue,
  envPushMasterSystemServer,
} from "../../../../helpers/fakebackend_helper";
import EnvPushModal from "./EnvPushModal";

// The DigitalOcean environment variable table (Change #100749). Each variable
// is defined once; its values are aimed at a server type, an environment, one
// server or everywhere, and the most specific match wins for each server.
// Secret values are never stored: they are typed at push time.

export const SCOPES = [
  { value: "RUN_AND_BUILD_TIME", label: "Run and build time" },
  { value: "RUN_TIME", label: "Run time" },
  { value: "BUILD_TIME", label: "Build time" },
];

// Mirrors EnvVarLibrary::matchScore on the backend
const matchScore = (value, server) => {
  let score = 0;
  if (value.MasterSystemServer_Id) {
    if (Number(value.MasterSystemServer_Id) !== Number(server.Id)) return -1;
    score += 4;
  }
  if (value.ServerType) {
    if (String(value.ServerType).toLowerCase() !== String(server.ServerType || "").toLowerCase())
      return -1;
    score += 2;
  }
  if (value.Environment) {
    if (
      String(value.Environment).toLowerCase() !== String(server.Environment || "").toLowerCase()
    )
      return -1;
    score += 1;
  }
  return score;
};

const resolveValue = (variable, server) => {
  if (!Number(variable.FlagActive)) return null;
  let best = null;
  let bestScore = -1;
  (variable.values || []).forEach((value) => {
    const score = matchScore(value, server);
    if (score >= 0 && score >= bestScore) {
      best = value;
      bestScore = score;
    }
  });
  return best;
};

const emptyVar = { EnvKey: "", Description: "", Scope: "RUN_AND_BUILD_TIME", Type: "GENERAL", FlagActive: 1 };
const emptyValue = { ServerType: "", Environment: "", MasterSystemServer_Id: "", Value: "" };

const EnvVarTable = ({ onApplied }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vars, setVars] = useState([]);
  const [servers, setServers] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("variables");

  const [varForm, setVarForm] = useState(null); // null = closed
  const [valueForm, setValueForm] = useState(null); // { variable, ...fields }
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // "var-<id>" | "val-<id>"

  const [pushOpen, setPushOpen] = useState(false);
  const [confirmPushAll, setConfirmPushAll] = useState(false);
  const [pushAllResults, setPushAllResults] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMasterEnvVarList();
      if (res?.status === 200) {
        setVars(Array.isArray(res.masterenvvar_list) ? res.masterenvvar_list : []);
        setServers(Array.isArray(res.mastersystemserver_list) ? res.mastersystemserver_list : []);
      } else {
        setError(res?.message || "Could not load the variable table");
      }
    } catch (e) {
      setError("Could not load the variable table");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const serverTypes = useMemo(
    () => [...new Set(servers.map((s) => s.ServerType).filter(Boolean))].sort(),
    [servers]
  );
  const environments = useMemo(
    () => [...new Set(servers.map((s) => s.Environment).filter(Boolean))].sort(),
    [servers]
  );
  const deployable = useMemo(() => servers.filter((s) => s.DigitalOceanAppId), [servers]);

  const filtered = vars.filter((v) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(v.EnvKey).toLowerCase().includes(q) ||
      String(v.Description || "").toLowerCase().includes(q)
    );
  });

  const serverName = (id) => {
    const s = servers.find((x) => Number(x.Id) === Number(id));
    return s ? `${s.Name} ${s.ServerType}` : `server #${id}`;
  };

  const targetLabel = (value) => {
    const parts = [];
    if (value.ServerType) parts.push(value.ServerType);
    if (value.Environment) parts.push(value.Environment);
    if (value.MasterSystemServer_Id) parts.push(serverName(value.MasterSystemServer_Id));
    return parts.length ? parts.join(" / ") : "everywhere";
  };

  // --- save helpers ---
  const run = async (call, okMessage) => {
    setSaving(true);
    try {
      const res = await call();
      if (res?.status === 200) {
        toast.success(okMessage);
        setSaving(false);
        await load();
        return true;
      }
      toast.error(res?.message || "Save failed");
    } catch (e) {
      toast.error("Save failed");
    }
    setSaving(false);
    return false;
  };

  const saveVar = async () => {
    const body = [
      {
        ...(varForm.Id ? { Id: varForm.Id } : {}),
        EnvKey: varForm.EnvKey.trim(),
        Description: varForm.Description,
        Scope: varForm.Scope,
        Type: varForm.Type,
        FlagActive: varForm.FlagActive ? 1 : 0,
      },
    ];
    const ok = await run(
      () => (varForm.Id ? updateMasterEnvVar(body) : createMasterEnvVar(body)),
      varForm.Id ? "Variable updated" : "Variable created"
    );
    if (ok) setVarForm(null);
  };

  const saveValue = async () => {
    const body = [
      {
        ...(valueForm.Id ? { Id: valueForm.Id } : {}),
        MasterEnvVar_Id: valueForm.variable.Id,
        ServerType: valueForm.ServerType,
        Environment: valueForm.Environment,
        MasterSystemServer_Id: valueForm.MasterSystemServer_Id || null,
        Value: valueForm.variable.Type === "SECRET" ? null : valueForm.Value,
      },
    ];
    const ok = await run(
      () => (valueForm.Id ? updateMasterEnvVarValue(body) : createMasterEnvVarValue(body)),
      valueForm.Id ? "Value updated" : "Value added"
    );
    if (ok) setValueForm(null);
  };

  const removeVar = (variable) => {
    setConfirmDelete(null);
    run(() => deleteMasterEnvVar([variable.Id]), `${variable.EnvKey} deleted`);
  };

  const removeValue = (value) => {
    setConfirmDelete(null);
    run(() => deleteMasterEnvVarValue([value.Id]), "Value removed");
  };

  const pushAll = async () => {
    setConfirmPushAll(false);
    setSaving(true);
    try {
      const res = await envPushMasterSystemServer({ Servers: deployable.map((s) => s.Id) });
      setPushAllResults(Array.isArray(res?.results) ? res.results : []);
      if (res?.status === 200) toast.success(res?.message || "Push complete");
      else toast.error(res?.message || "Push failed");
      const changed = (res?.results || [])
        .filter((r) => Number(r.Success) && (r.Changes || []).length)
        .map((r) => r.Id);
      if (changed.length && onApplied) onApplied(changed);
    } catch (e) {
      toast.error("Push failed");
    }
    setSaving(false);
  };

  const keyValid = varForm ? /^[A-Za-z_][A-Za-z0-9_]*$/.test(varForm.EnvKey.trim()) : false;

  return (
    <Card>
      <CardBody>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <h4 className="card-title mb-0">Environment Variables</h4>
            <div className="text-muted small">
              Define each variable once and aim its values at a server type, an environment
              or one server. The most specific value wins. Secret values are never stored, so
              they are typed in when you push.
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Input
              bsSize="sm"
              style={{ width: 180 }}
              placeholder="Search keys..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <ButtonGroup size="sm">
              <Button
                color={view === "variables" ? "primary" : "light"}
                className="border"
                onClick={() => setView("variables")}
              >
                Variables
              </Button>
              <Button
                color={view === "servers" ? "primary" : "light"}
                className="border"
                onClick={() => setView("servers")}
              >
                By server
              </Button>
            </ButtonGroup>
            <Button color="primary" size="sm" onClick={() => setVarForm({ ...emptyVar })}>
              <i className="ri-add-line align-middle me-1"></i>
              New variable
            </Button>
            <Button
              color="success"
              size="sm"
              disabled={!deployable.length}
              onClick={() => setPushOpen(true)}
            >
              <i className="ri-upload-cloud-2-line align-middle me-1"></i>
              Push...
            </Button>
            {confirmPushAll ? (
              <>
                <Button color="danger" size="sm" disabled={saving} onClick={pushAll}>
                  Push to {deployable.length} app{deployable.length === 1 ? "" : "s"}
                </Button>
                <Button color="light" size="sm" onClick={() => setConfirmPushAll(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                color="light"
                size="sm"
                className="border"
                disabled={!deployable.length || saving}
                title="Push every deployable server. Secrets are left as they are and nothing is removed."
                onClick={() => setConfirmPushAll(true)}
              >
                Push all
              </Button>
            )}
          </div>
        </div>

        {error && (
          <Alert color="danger" className="py-2 small">
            {error}
          </Alert>
        )}

        {pushAllResults.length > 0 && (
          <Alert color="light" className="py-2 small border" toggle={() => setPushAllResults([])}>
            {pushAllResults.map((r) => (
              <div key={r.Id} className={Number(r.Success) ? "text-success" : "text-danger"}>
                {r.Name || `Server ${r.Id}`}: {r.Message}
                {(r.Skipped || []).length
                  ? ` (${r.Skipped.length} secret${r.Skipped.length === 1 ? "" : "s"} still need a value)`
                  : ""}
              </div>
            ))}
          </Alert>
        )}

        {view === "variables" ? (
          <div className="table-responsive">
            <Table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Key</th>
                  <th>Type</th>
                  <th>Scope</th>
                  <th>Values</th>
                  <th className="text-end"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      <Spinner size="sm" className="me-2" />
                      Loading variables...
                    </td>
                  </tr>
                ) : filtered.length ? (
                  filtered.map((variable) => (
                    <tr key={variable.Id} className={Number(variable.FlagActive) ? "" : "opacity-50"}>
                      <td>
                        <div className="font-monospace fw-medium">{variable.EnvKey}</div>
                        {variable.Description && (
                          <div className="text-muted small">{variable.Description}</div>
                        )}
                        {!Number(variable.FlagActive) && (
                          <Badge color="secondary" className="mt-1">
                            Inactive
                          </Badge>
                        )}
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            variable.Type === "SECRET"
                              ? "bg-danger-subtle text-danger"
                              : "bg-secondary-subtle text-secondary"
                          }`}
                        >
                          {variable.Type}
                        </span>
                      </td>
                      <td className="small">
                        {SCOPES.find((s) => s.value === variable.Scope)?.label || variable.Scope}
                      </td>
                      <td>
                        {(variable.values || []).length === 0 && (
                          <span className="text-muted small">No values, so on no server</span>
                        )}
                        {(variable.values || []).map((value) => (
                          <div key={value.Id} className="d-flex align-items-center gap-2 small mb-1">
                            <span className="badge bg-info-subtle text-info">{targetLabel(value)}</span>
                            <span
                              className="font-monospace text-truncate"
                              style={{ maxWidth: 260 }}
                              title={variable.Type === "SECRET" ? "" : value.Value}
                            >
                              {variable.Type === "SECRET" ? (
                                <span className="text-muted">typed at push</span>
                              ) : value.Value === "" ? (
                                <span className="text-muted">(empty)</span>
                              ) : (
                                value.Value
                              )}
                            </span>
                            <Button
                              color="link"
                              size="sm"
                              className="p-0"
                              title="Edit value"
                              onClick={() =>
                                setValueForm({
                                  variable,
                                  Id: value.Id,
                                  ServerType: value.ServerType || "",
                                  Environment: value.Environment || "",
                                  MasterSystemServer_Id: value.MasterSystemServer_Id || "",
                                  Value: value.Value || "",
                                })
                              }
                            >
                              <i className="ri-pencil-line" />
                            </Button>
                            {confirmDelete === `val-${value.Id}` ? (
                              <>
                                <Button
                                  color="danger"
                                  size="sm"
                                  className="py-0"
                                  disabled={saving}
                                  onClick={() => removeValue(value)}
                                >
                                  Remove
                                </Button>
                                <Button
                                  color="light"
                                  size="sm"
                                  className="py-0"
                                  onClick={() => setConfirmDelete(null)}
                                >
                                  Keep
                                </Button>
                              </>
                            ) : (
                              <Button
                                color="link"
                                size="sm"
                                className="p-0 text-danger"
                                title="Remove value"
                                onClick={() => setConfirmDelete(`val-${value.Id}`)}
                              >
                                <i className="ri-close-line" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 small"
                          onClick={() => setValueForm({ variable, ...emptyValue })}
                        >
                          <i className="ri-add-line" /> Add value
                        </Button>
                      </td>
                      <td className="text-end text-nowrap">
                        {confirmDelete === `var-${variable.Id}` ? (
                          <>
                            <Button
                              color="danger"
                              size="sm"
                              className="me-1"
                              disabled={saving}
                              onClick={() => removeVar(variable)}
                            >
                              Delete
                            </Button>
                            <Button color="light" size="sm" onClick={() => setConfirmDelete(null)}>
                              Keep
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              color="light"
                              size="sm"
                              className="me-1"
                              title="Edit variable"
                              onClick={() =>
                                setVarForm({
                                  Id: variable.Id,
                                  EnvKey: variable.EnvKey,
                                  Description: variable.Description || "",
                                  Scope: variable.Scope,
                                  Type: variable.Type,
                                  FlagActive: Number(variable.FlagActive) ? 1 : 0,
                                  originalType: variable.Type,
                                })
                              }
                            >
                              <i className="ri-pencil-line" />
                            </Button>
                            <Button
                              color="light"
                              size="sm"
                              title="Delete variable"
                              onClick={() => setConfirmDelete(`var-${variable.Id}`)}
                            >
                              <i className="ri-delete-bin-line text-danger" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      {vars.length
                        ? "No variables match the search."
                        : "No variables yet. Create one, or use Push... > Import to bring in what an app already has."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="table-responsive">
            <Table size="sm" className="table align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Key</th>
                  {servers.map((s) => (
                    <th key={s.Id} className="text-nowrap">
                      {s.Name}
                      <div className="small text-muted text-uppercase fw-normal">
                        {s.ServerType} / {s.Environment}
                        {!s.DigitalOceanAppId ? " (no app)" : ""}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((variable) => (
                  <tr key={variable.Id}>
                    <td className="font-monospace small fw-medium">{variable.EnvKey}</td>
                    {servers.map((s) => {
                      const value = resolveValue(variable, s);
                      return (
                        <td
                          key={s.Id}
                          className="font-monospace small text-truncate"
                          style={{ maxWidth: 180 }}
                          title={value ? `from ${targetLabel(value)}` : ""}
                        >
                          {!value ? (
                            <span className="text-muted">-</span>
                          ) : variable.Type === "SECRET" ? (
                            <span className="text-danger">secret</span>
                          ) : (
                            value.Value
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      {/* variable create / edit */}
      <AppModal
        open={!!varForm}
        onClose={() => setVarForm(null)}
        title={varForm?.Id ? `Edit ${varForm.EnvKey}` : "New variable"}
        icon="ri-key-2-line"
        loading={saving}
        onSubmit={saveVar}
        submitDisabled={!keyValid}
      >
        {varForm && (
          <Row className="g-2">
            <Col md="12">
              <Label className="form-label small mb-1">Key</Label>
              <Input
                bsSize="sm"
                className="font-monospace"
                value={varForm.EnvKey}
                invalid={varForm.EnvKey !== "" && !keyValid}
                placeholder="MY_NEW_VAR"
                onChange={(e) => setVarForm({ ...varForm, EnvKey: e.target.value })}
              />
              {varForm.Id && (
                <div className="small text-muted mt-1">
                  Renaming leaves the old key on the apps until a push removes it.
                </div>
              )}
            </Col>
            <Col md="12">
              <Label className="form-label small mb-1">Description</Label>
              <Input
                bsSize="sm"
                type="textarea"
                rows="2"
                value={varForm.Description}
                onChange={(e) => setVarForm({ ...varForm, Description: e.target.value })}
              />
            </Col>
            <Col md="6">
              <Label className="form-label small mb-1">Scope</Label>
              <Input
                type="select"
                bsSize="sm"
                value={varForm.Scope}
                onChange={(e) => setVarForm({ ...varForm, Scope: e.target.value })}
              >
                {SCOPES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="6">
              <Label className="form-label small mb-1">Type</Label>
              <Input
                type="select"
                bsSize="sm"
                value={varForm.Type}
                onChange={(e) => setVarForm({ ...varForm, Type: e.target.value })}
              >
                <option value="GENERAL">General (value stored here)</option>
                <option value="SECRET">Secret (typed at push)</option>
              </Input>
              {varForm.Id && varForm.originalType !== "SECRET" && varForm.Type === "SECRET" && (
                <div className="small text-warning mt-1">
                  Its stored values will be cleared. Apps keep what they have.
                </div>
              )}
            </Col>
            <Col md="12">
              <div className="form-check mt-2">
                <Input
                  type="checkbox"
                  className="form-check-input"
                  id="envvar-active"
                  checked={!!varForm.FlagActive}
                  onChange={(e) => setVarForm({ ...varForm, FlagActive: e.target.checked ? 1 : 0 })}
                />
                <Label className="form-check-label small" for="envvar-active">
                  Active (inactive variables are not pushed, and become removable on the apps)
                </Label>
              </div>
            </Col>
          </Row>
        )}
      </AppModal>

      {/* value create / edit */}
      <AppModal
        open={!!valueForm}
        onClose={() => setValueForm(null)}
        title={valueForm ? `${valueForm.Id ? "Edit" : "Add"} value: ${valueForm.variable.EnvKey}` : ""}
        icon="ri-equalizer-line"
        loading={saving}
        onSubmit={saveValue}
      >
        {valueForm && (
          <Row className="g-2">
            <Col md="12" className="small text-muted">
              Leave a target on Any to match all of them. The most specific value wins: one
              server, then type and environment, then type, then environment, then everywhere.
            </Col>
            <Col md="4">
              <Label className="form-label small mb-1">Server type</Label>
              <Input
                type="select"
                bsSize="sm"
                value={valueForm.ServerType}
                onChange={(e) => setValueForm({ ...valueForm, ServerType: e.target.value })}
              >
                <option value="">Any</option>
                {serverTypes.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="4">
              <Label className="form-label small mb-1">Environment</Label>
              <Input
                type="select"
                bsSize="sm"
                value={valueForm.Environment}
                onChange={(e) => setValueForm({ ...valueForm, Environment: e.target.value })}
              >
                <option value="">Any</option>
                {environments.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="4">
              <Label className="form-label small mb-1">One server</Label>
              <Input
                type="select"
                bsSize="sm"
                value={valueForm.MasterSystemServer_Id}
                onChange={(e) =>
                  setValueForm({ ...valueForm, MasterSystemServer_Id: e.target.value })
                }
              >
                <option value="">Any</option>
                {servers.map((s) => (
                  <option key={s.Id} value={s.Id}>
                    {s.Name} {s.ServerType} ({s.Environment})
                  </option>
                ))}
              </Input>
            </Col>
            <Col md="12">
              <Label className="form-label small mb-1">Value</Label>
              {valueForm.variable.Type === "SECRET" ? (
                <div className="small text-muted">
                  Secret: no value is stored. This row only decides which servers get the
                  secret; type the value in Push.
                </div>
              ) : (
                <Input
                  bsSize="sm"
                  type="textarea"
                  rows="2"
                  className="font-monospace"
                  value={valueForm.Value}
                  onChange={(e) => setValueForm({ ...valueForm, Value: e.target.value })}
                />
              )}
            </Col>
          </Row>
        )}
      </AppModal>

      <EnvPushModal
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        servers={deployable}
        onImported={load}
        onApplied={onApplied}
      />
    </Card>
  );
};

export default EnvVarTable;
